import React, { useState } from 'react';
import { Search, ArrowRight, Users, Building2, ArrowLeft, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';
import Modal from './WaModal';
import Avatar from './WaAvatar';
import { type IDepartamento } from '../../hooks/useDepartamentos';
import { api } from '../../services/api';
import waApi from '../../services/whatsapp/waApi';

interface Agent {
  id: number;
  name: string;
  email: string;
  online?: boolean;
}

interface TransferModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTransfer: (
    agentId: string | null,
    agentName: string | null,
    department: string,
    reason: string,
  ) => void;
}

export default function TransferModal({ isOpen, onClose, onTransfer }: TransferModalProps) {
  const [step, setStep] = useState<'department' | 'agent'>('department');
  const [selectedDept, setSelectedDept] = useState<IDepartamento | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [reason, setReason] = useState('');
  const [search, setSearch] = useState('');

  // Busca os departamentos para os quais o usuario PODE transferir. O backend
  // (GET /conversations/departments) ja filtra pelas permissoes do usuario
  // (admin/ver_todos veem todos; os demais so o proprio + liberados), entao o
  // front nao recalcula a regra — fonte de verdade unica, sem divergir.
  const { data: deptData, isLoading: loadingDepts } = useQuery({
    queryKey: ['DepartamentosTransfer'],
    queryFn: async () => {
      const res = await waApi.get('/conversations/departments');
      return (res.data ?? []) as IDepartamento[];
    },
    enabled: isOpen,
  });

  const departamentos = (deptData ?? []).filter((d: IDepartamento) => d.status === 'ativo');

  // Busca agentes vinculados ao departamento selecionado
  const fetchAgents = async (deptId: number) => {
    setLoadingAgents(true);
    try {
      const res = await api.get(`/api/v1/usuarios/by-departamentos/`, {
        params: { departamento_ids: deptId },
      });
      const users = res.data?.data ?? [];
      setAgents(
        users.map((u: any) => ({
          id: u.id,
          name: u.nome,
          email: u.email,
        })),
      );
    } catch {
      setAgents([]);
    } finally {
      setLoadingAgents(false);
    }
  };

  const handleClose = () => {
    setStep('department');
    setSelectedDept(null);
    setSelectedAgent(null);
    setAgents([]);
    setReason('');
    setSearch('');
    onClose();
  };

  const handleSelectDept = (dept: IDepartamento) => {
    setSelectedDept(dept);
    setSelectedAgent(null);
    setStep('agent');
    setSearch('');
    fetchAgents(dept.id);
  };

  const handleTransferToDept = () => {
    if (!selectedDept) return;
    onTransfer(null, null, selectedDept.nome, reason);
    handleClose();
  };

  const handleTransferToAgent = () => {
    if (!selectedAgent || !selectedDept) return;
    onTransfer(String(selectedAgent.id), selectedAgent.name, selectedDept.nome, reason);
    handleClose();
  };

  const filteredDepts = departamentos.filter((d: IDepartamento) =>
    search ? d.nome.toLowerCase().includes(search.toLowerCase()) : true,
  );

  const filteredAgents = agents.filter(a =>
    search ? a.name.toLowerCase().includes(search.toLowerCase()) : true,
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Transferir Atendimento" size="md">
      <div className="space-y-4">
        {/* Search */}
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={step === 'department' ? 'Buscar departamento...' : 'Buscar atendente...'}
            className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-9 pr-4 text-sm focus:border-green-400 focus:outline-none focus:ring-2 focus:ring-green-500/40"
          />
        </div>

        {/* Step 1: Select department */}
        {step === 'department' && (
          <div className="space-y-1.5">
            <p className="px-1 text-xs font-medium uppercase tracking-wider text-gray-400">
              Selecione o departamento
            </p>
            {(() => {
              if (loadingDepts) {
                return (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 size={20} className="animate-spin text-green-600" />
                  </div>
                );
              }
              if (filteredDepts.length === 0) {
                return (
                  <p className="py-6 text-center text-sm text-gray-400">
                    Nenhum departamento encontrado
                  </p>
                );
              }
              return filteredDepts.map((dept: IDepartamento) => (
                <button
                  type="button"
                  key={dept.id}
                  onClick={() => handleSelectDept(dept)}
                  className="group flex w-full items-center justify-between rounded-xl px-4 py-3 transition-colors hover:bg-gray-50 active:bg-gray-100"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-50">
                      <Building2 size={18} className="text-green-600" />
                    </div>
                    <span className="text-sm font-medium text-gray-800">{dept.nome}</span>
                  </div>
                  <ArrowRight
                    size={16}
                    className="text-gray-300 transition-colors group-hover:text-gray-500"
                  />
                </button>
              ));
            })()}
          </div>
        )}

        {/* Step 2: Select agent (optional) */}
        {step === 'agent' && selectedDept && (
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => {
                setStep('department');
                setSelectedAgent(null);
                setAgents([]);
                setSearch('');
              }}
              className="flex items-center gap-1 text-xs font-medium text-green-600 hover:text-green-700"
            >
              <ArrowLeft size={12} />
              Voltar para departamentos
            </button>

            <p className="px-1 text-xs font-medium uppercase tracking-wider text-gray-400">
              {selectedDept.nome} - Selecione o atendente (opcional)
            </p>

            {(() => {
              if (loadingAgents) {
                return (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 size={20} className="animate-spin text-green-600" />
                  </div>
                );
              }
              if (filteredAgents.length === 0) {
                return (
                  <p className="py-4 text-center text-sm text-gray-400">
                    Nenhum atendente encontrado neste departamento
                  </p>
                );
              }
              return filteredAgents.map(agent => (
                <button
                  type="button"
                  key={agent.id}
                  onClick={() => setSelectedAgent(selectedAgent?.id === agent.id ? null : agent)}
                  className={clsx(
                    'flex w-full items-center gap-3 rounded-xl px-4 py-3 transition-all',
                    selectedAgent?.id === agent.id
                      ? 'bg-green-50 ring-2 ring-green-500'
                      : 'hover:bg-gray-50 active:bg-gray-100',
                  )}
                >
                  <Avatar name={agent.name} size="sm" />
                  <div className="flex-1 text-left">
                    <span className="block text-sm font-medium text-gray-800">{agent.name}</span>
                    <span className="text-xs text-gray-400">{agent.email}</span>
                  </div>
                </button>
              ));
            })()}

            {/* Reason + actions */}
            <div className="space-y-3 border-t border-gray-100 pt-2">
              <div>
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor="transfer-reason" className="text-xs font-medium text-gray-500">
                  Motivo da transferencia
                </label>
                <textarea
                  id="transfer-reason"
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder="Descreva o motivo (opcional)..."
                  rows={2}
                  className="mt-1 w-full resize-none rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/40"
                />
              </div>

              <div className="flex flex-col gap-2">
                {selectedAgent ? (
                  <button
                    type="button"
                    onClick={handleTransferToAgent}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-green-600 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-green-700"
                  >
                    <Users size={16} />
                    Transferir para {selectedAgent.name}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleTransferToDept}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-green-600 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-green-700"
                  >
                    <Building2 size={16} />
                    Transferir para {selectedDept.nome}
                  </button>
                )}

                {selectedAgent && (
                  <button
                    type="button"
                    onClick={() => setSelectedAgent(null)}
                    className="w-full py-2 text-xs font-medium text-gray-500 transition-colors hover:text-gray-700"
                  >
                    Ou transferir somente para o departamento
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
